import type { ColumnDef } from '@tanstack/react-table';

import { SiteDeliveryType, SiteState, SiteType } from 'data/__generated__';
import { multiSelectColumn, dateColumn } from 'components/DataTable';

import { StatusTag } from 'pages/Sites/components/StatusTag';
import { TypeTag } from 'pages/Sites/components/TypeTag';
import { DeliveryTypeTag } from 'pages/Sites/components/DeliveryTypeTag';
import { SITE_STATES_LABELS } from 'pages/Sites/constants/states';
import { SITE_TYPES_LABELS, SITE_DELIVERY_TYPES_LABELS } from 'pages/Sites/constants/types';

// Shape shared by the two embedded site tables (organization details & totem details). Both `totem.sites`
// selections resolve to this; the organization table additionally injects `totemFullname` (the owning totem's
// `[name] #[number]` label) to show which totem each site belongs to.
export type EmbeddedSiteRow = {
    _id: string;
    name: string;
    state: SiteState;
    type: SiteType;
    deliveryType: SiteDeliveryType;
    createdAt: number;
    totemFullname?: string | null;
};

export type EmbeddedSitesFilterInput = {
    searchValue: string;
    state: SiteState[];
    type: SiteType[];
    deliveryType: SiteDeliveryType[];
};

const EmptyCell = () => <span className="text-muted-foreground">—</span>;

export const getEmbeddedSitesColumns = ({
    showTotemColumn = false,
}: {
    showTotemColumn?: boolean;
} = {}): ColumnDef<EmbeddedSiteRow, unknown>[] => {
    const columns: ColumnDef<EmbeddedSiteRow, unknown>[] = [
        {
            accessorKey: 'name',
            header: 'Nom',
            meta: { defaultVisible: true },
        },
    ];

    if (showTotemColumn) {
        columns.push({
            id: 'totemFullname',
            header: 'Totem',
            meta: { defaultVisible: true },
            accessorFn: (row) => row.totemFullname ?? '',
            cell: ({ row }) => row.original.totemFullname || <EmptyCell />,
        });
    }

    columns.push(
        multiSelectColumn<EmbeddedSiteRow, EmbeddedSitesFilterInput>({
            accessorKey: 'type',
            header: 'Type',
            filterKey: 'type',
            defaultVisible: true,
            options: Object.values(SiteType).map((type) => ({ value: type, label: SITE_TYPES_LABELS[type] })),
            renderOption: (option) => <TypeTag type={option.value as SiteType} />,
            cell: ({ row }) => <TypeTag type={row.original.type} />,
        }),
        multiSelectColumn<EmbeddedSiteRow, EmbeddedSitesFilterInput>({
            accessorKey: 'state',
            header: 'Statut',
            filterKey: 'state',
            defaultVisible: true,
            options: Object.values(SiteState).map((state) => ({ value: state, label: SITE_STATES_LABELS[state] })),
            renderOption: (option) => <StatusTag state={option.value as SiteState} />,
            cell: ({ row }) => <StatusTag state={row.original.state} />,
        }),
        multiSelectColumn<EmbeddedSiteRow, EmbeddedSitesFilterInput>({
            accessorKey: 'deliveryType',
            header: 'Type de livraison',
            filterKey: 'deliveryType',
            defaultVisible: true,
            options: Object.values(SiteDeliveryType).map((deliveryType) => ({
                value: deliveryType,
                label: SITE_DELIVERY_TYPES_LABELS[deliveryType],
            })),
            renderOption: (option) => <DeliveryTypeTag deliveryType={option.value as SiteDeliveryType} />,
            cell: ({ row }) => <DeliveryTypeTag deliveryType={row.original.deliveryType} />,
        }),
        dateColumn<EmbeddedSiteRow, EmbeddedSitesFilterInput>({
            accessorKey: 'createdAt',
            header: 'Date de création',
            defaultVisible: true,
        }),
    );

    return columns;
};
